/**
 * QueueCapCell — a pool's live "{active} / {cap}" capacity meter for a queue
 * row. Resolves the row's pool from the QueueListResult.pools summary and tints
 * red when over cap. Renders "—" when the pool is uncapped or unknown.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import { Gauge } from 'lucide-react';
import {
  isOverCapacity,
  type FlowLayer,
  type QueuePoolSummary,
} from '@trafficpulse/shared';

import { cn } from '@/lib/utils';

/** Find the pool summary backing a queue row (layer + pool id). */
export function findPool(
  pools: QueuePoolSummary[],
  layer: FlowLayer,
  poolId: string,
): QueuePoolSummary | undefined {
  return pools.find((p) => p.layer === layer && p.poolId === poolId);
}

const QueueCapCell: FC<{ pool?: QueuePoolSummary }> = ({ pool }) => {
  if (!pool || pool.cap == null) {
    return <span className="text-muted-foreground">—</span>;
  }
  const over = isOverCapacity(pool.cap, pool.active);
  const pct = Math.min(100, (pool.active / Math.max(1, pool.cap)) * 100);
  const tone = over ? 'bg-destructive' : pct >= 80 ? 'bg-warning' : 'bg-primary';
  return (
    <div className="ml-auto min-w-[4.5rem]">
      <span className="flex items-center justify-end gap-1 text-[11px] tabular-nums">
        <Gauge
          className={cn('h-3 w-3 shrink-0', over ? 'text-destructive' : 'text-muted-foreground')}
          aria-hidden
        />
        <span className={cn('font-medium', over ? 'text-destructive' : 'text-foreground')}>
          {pool.active}/{pool.cap}
        </span>
      </span>
      <div className="mt-0.5 h-1 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={cn('h-full rounded-full transition-all', tone)}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

export default QueueCapCell;
